import React from 'react'
import { useTranslation } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { useSelector, useDispatch } from 'react-redux'
import { IAppState } from 'infra/AppState'
import { refreshMerchantTransactions } from 'state/transaction/transaction.actions'
import { THEME } from 'components/theme'

const useStyles = createUseStyles({
  // Rounded pill next to the business-name pill. Neutral at rest; a clear green
  // tint (bg + border + icon/label all go brand-green) on hover.
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 36,
    padding: '0 14px 0 12px',
    border: `1px solid ${THEME.shellBorder}`,
    borderRadius: 18,
    backgroundColor: THEME.brandWhite,
    color: THEME.brandBlackAlt,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease',
    '&:hover': {
      backgroundColor: THEME.brandLightGreen,
      borderColor: THEME.brandGreen,
      color: '#117631'
    },
    '&:active': {
      backgroundColor: THEME.brandLightGreen
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
      backgroundColor: THEME.brandWhite,
      borderColor: THEME.shellBorder,
      color: THEME.brandBlackAlt
    }
  },
  icon: {
    width: 16,
    height: 16,
    flexShrink: 0
  },
  spinning: {
    animation: '$spin 0.8s linear infinite'
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  }
})

/**
 * Header pull-to-refresh control. Triggers a business-wide incremental sync of
 * customer transactions (latest changes since the last sync) and spins while it
 * runs.
 */
const RefreshButton = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isRefreshing = useSelector((app: IAppState) => app.Transactions.isRefreshingLedger)

  return (
    <button
      type="button"
      className={classes.button}
      disabled={isRefreshing}
      onClick={() => dispatch(refreshMerchantTransactions())}
      title={t('dashboard:Refresh')}
      aria-label={t('dashboard:Refresh')}
    >
      <svg
        className={`${classes.icon} ${isRefreshing ? classes.spinning : ''}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v6h-6" />
      </svg>
      {t('dashboard:Refresh')}
    </button>
  )
}

export default RefreshButton
